import React from 'react';
import Input from '../../forms/Input';
import { QuestRoute } from '../../api/QuestRoutes';
import Checkbox from '../../forms/Checkbox';
import Table, { Ctd } from '../../table/Table';
import { formatCurrency, generateId } from '../../utility/functions';
import Button from '../../forms/Button';
import PageBreak from '../../layout/PageBreak';
import HamQuest from '../../api/HamQuestGiver';
import GetState from '../../GetState';
import Form from '../../forms/Form';
import { userRoles } from '../../RoleProtected';
import ManagementRow from '../../table/ManagementRow';
import ModalFactory from '../../Modal';
import CheckMark from '../../CheckMark';

const toNullableNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const normalizeDetails = (details) => {
    if (Array.isArray(details)) {
        return details.join('\n');
    }

    return details ?? '';
};

const buildProductPayload = (values, initial = {}) => {
    const source = { ...initial, ...values };

    return {
        appProductId: toNullableNumber(source.appProductId ?? source.productId) || 0,
        active: source.active ?? source.enabled ?? false,
        productType: source.productType ?? source.appProductType ?? 'EGiftCard',
        kioskItemId: toNullableNumber(source.kioskItemId),
        useKioskItemName: Boolean(source.useKioskItemName),
        name: source.name ?? '',
        useKioskItemDetails: Boolean(source.useKioskItemDetails),
        details: normalizeDetails(source.details),
        quantity: toNullableNumber(source.quantity) ?? 1,
        useKioskItemPrice: Boolean(source.useKioskItemPrice),
        price: toNullableNumber(source.price),
        locations: Array.isArray(source.locations) ? source.locations : [],
        terminalIds: Array.isArray(source.terminalIds) ? source.terminalIds : [],
        logoColor: toNullableNumber(source.logoColor),
        logoImageIndex: toNullableNumber(source.logoImageIndex ?? source.logo?.imageIndex),
        purchaseLimit: toNullableNumber(source.purchaseLimit),
        purchaseLimitStartDate: source.purchaseLimitStartDate || null,
        taxable: Boolean(source.taxable),
        value: toNullableNumber(source.value) ?? toNullableNumber(source.price),
    };
};

const EGift = ({ ...props }) => {

    const FulfillmentForm = ({ values }) => (
        <>
            <Checkbox
                label='Active'
                name='active'
            />
            <Input
                label='Name'
                name='name'
                required
            />
            <Input
                label='Description'
                name='details'
            />
            <Input
                label='Price'
                name='price'
                type='price'
                required
            />
            <Checkbox
                label='Is taxed'
                name='taxable'
            />
            <Input
                label='Value'
                name='value'
                type='price'
                min={values.price}
                required
            />
        </>
    )

    const [eGifts, refreshEGifts] = GetState(QuestRoute.products.eGiftCards);

    const deleteProduct = (product) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${product.name}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Product',
                body: `Deleting ${product.name}`,
                callback: () => HamQuest.delete(QuestRoute.products.edit(product.appProductId))
            },
            confirmed: {
                title: 'Success',
                body: `${product.name} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshEGifts()
        })
    }

    const editProduct = (product) => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${product.name}`,
            body: (
                <Form
                    name='eGiftCardSettings'
                    inputs={{ ...product }}
                    onSubmit={values => {
                        const payload = buildProductPayload(values, product);
                        return HamQuest.put(QuestRoute.products.edit(product.appProductId), payload)
                            .then(() => {
                                refreshEGifts();
                                ModalFactory.close(id);
                            })
                    }}
                    text='Update'
                >
                    {({ values }) => <FulfillmentForm values={values} editing />}
                </Form>
            )
        })
    }

    const addNewEGift = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add App Credit`,
            body: (
                <Form
                    name='eGiftCardSettings'
                    inputs={{
                        appProductId: 0,
                        active: true,
                        productType: 'EGiftCard',
                        kioskItemId: null,
                        useKioskItemName: false,
                        name: '',
                        useKioskItemDetails: false,
                        details: '',
                        quantity: 1,
                        useKioskItemPrice: false,
                        price: 0,
                        value: 0,
                        locations: [],
                        terminalIds: [],
                        logoColor: null,
                        logoImageIndex: 0,
                        purchaseLimit: null,
                        purchaseLimitStartDate: null,
                        taxable: false,
                    }}
                    onSubmit={values => {
                        const payload = buildProductPayload(values);
                        return HamQuest.post(QuestRoute.products.eGiftCards, payload)
                            .then(() => {
                                refreshEGifts();
                                ModalFactory.close(id);
                            })
                    }}
                    text='Add App Credit'
                >
                    {({ values }) => <FulfillmentForm values={values} />}
                </Form>
            )
        })
    }

    return (
        <PageBreak>
            <Table
                config={{
                    controls: [<Button roles={userRoles.editProducts} onClick={addNewEGift}>Add New App Credit</Button>],
                    data: eGifts,
                    headers: [
                        {
                            label: 'Name',
                            propKey: 'name',
                        }, {
                            label: 'Price',
                            propKey: 'price',
                            type: 'number'
                        }, {
                            label: 'Value',
                            propKey: 'value',
                            type: 'number'
                        }, {
                            label: 'Active',
                            propKey: 'active',
                            options: true,
                        },
                    ],
                    renderFormat: (e) => (
                        <ManagementRow
                            key={generateId()}
                            controls={[
                                <Button roles={userRoles.editProducts} key={generateId()} onClick={() => editProduct(e)}>Edit</Button>,
                                <Button roles={userRoles.editProducts} key={generateId()} onClick={() => deleteProduct(e)}>Delete</Button>
                            ]}
                        >
                            <td>{e.name}</td>
                            <td>{formatCurrency(e.price)}</td>
                            <td>{formatCurrency(e.value || e.price)}</td>
                            <Ctd><CheckMark checked={e.active} /></Ctd>
                        </ManagementRow>
                    )
                }}
            />
        </PageBreak>
    );
}

export default EGift;